import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import type { ArticleTeaser } from '@/types';
import { useContent } from '@data';
import { useT, useLang, pathFor } from '@/i18n';
import { SectionHeader, RevealWrapper, Tag } from '@components/ui';
import './Articles.css';

const ACCENTS = [
  'var(--color-maitrise)',
  'var(--color-piloter)',
  'var(--color-anticiper)',
  'var(--color-ameliorer)',
  'var(--color-ciel)',
  'var(--color-temps)',
  'var(--color-reagir)',
];

interface CardProps {
  article: ArticleTeaser;
  accent: string | undefined;
  more: string;
  themes: string;
}

/**
 * Une carte d'article.
 *
 * En francais l'article est heberge ici, donc `Link` (navigation interne).
 * En anglais il vit sur le blog Asterion : c'est alors une ancre externe, qui
 * s'ouvre dans un onglet et porte `rel="noopener"`.
 */
function ArticleCard({ article, accent, more, themes }: CardProps): JSX.Element {
  const style = { '--article-color': accent } as React.CSSProperties;
  const inner = (
    <>
      <div className="ArticleCard__accent" aria-hidden="true" />
      <h3 className="ArticleCard__name">{article.title}</h3>
      <p className="ArticleCard__desc">{article.excerpt}</p>
      <div className="ArticleCard__tags" role="list" aria-label={themes}>
        <Tag>{article.category}</Tag>
      </div>
      <span className="ArticleCard__more">{more}</span>
    </>
  );

  if (article.isExternal) {
    return (
      <a
        className="ArticleCard"
        href={article.href}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link className="ArticleCard" to={article.href} style={style}>
      {inner}
    </Link>
  );
}

export default function Articles(): JSX.Element {
  const { ARTICLE_TEASERS } = useContent();
  const t = useT();
  const lang = useLang();

  return (
    <section id="articles" className="section Articles">
      <SectionHeader
        chip={t.sections.articles.chip}
        chipVariant="m"
        title={t.sections.articles.title}
        subtitle={t.sections.articles.subtitle}
      />

      <div className="Articles__grid">
        {ARTICLE_TEASERS.map((article, i) => (
          <RevealWrapper key={article.slug} delay={(i % 4) + 1}>
            <ArticleCard
              article={article}
              accent={ACCENTS[i % ACCENTS.length]}
              more={t.sections.articles.readMore}
              themes={t.sections.articles.chip}
            />
          </RevealWrapper>
        ))}
      </div>

      <div className="Articles__foot">
        <Link className="Articles__all" to={pathFor('/articles', lang)}>
          {t.articles.browseAll}
        </Link>
        <a
          className="Articles__all Articles__all--ghost"
          href={t.articles.blogUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.articles.seeOnAsterion}
        </a>
      </div>
    </section>
  );
}
